import z from 'zod';
import {
  AllCharLabelSchema,
  GenericTipTapJsonSchema,
  StringBooleanSchema,
  SubjectSchema,
  UnixTimestampSchema,
  UUIDSchema,
} from '../common/schemas';

import dayjs from 'dayjs';
import { getObjectSizeInBytes } from '../common/utils';
import {
  DBLegacyExamExamIdSchema,
  LegacyExamExamIdSchema,
  LegacyExamSubjectSchema,
} from '../legacyExams/schemas';
import { DBLegacyTopicTopicIdSchema } from '../legacyTopics/schemas';
import { QuestionGuideType, QuestionType } from './enums';

/**
 * Shared & Helper Schemas
 */

export const DBLegacyExamQuestionGuideTypeSchema = z.enum(QuestionGuideType);
export const DBLegacyExamQuestionGuideLabelSchema = AllCharLabelSchema.min(1).max(256);
export const DBLegacyExamQuestionGuideContentSchema = GenericTipTapJsonSchema.refine(
  v => getObjectSizeInBytes(v) <= 50 * 1024
);
const mcqOptsOptionsCountError =
  'Multiple choice questions must have between 2 and 10 answer options';
const mcqOptsAnswerError = 'Please select the correct answer for the multiple choice question';
export const DBLegacyExamQuestionMcqOptsOptionsCountSchema = z
  .number({ error: mcqOptsOptionsCountError })
  .int({ error: mcqOptsOptionsCountError })
  .min(2, { error: mcqOptsOptionsCountError })
  .max(10, { error: mcqOptsOptionsCountError });
export const DBLegacyExamQuestionMcqOptsAnswerSchema = z
  .number({ error: mcqOptsAnswerError })
  .int({ error: mcqOptsAnswerError })
  .min(1, { error: mcqOptsAnswerError });
export const DBLegacyExamQuestionCriterionIdSchema = UUIDSchema;
export const DBLegacyExamQuestionCriterionContentSchema = GenericTipTapJsonSchema.refine(
  v => getObjectSizeInBytes(v) <= 3 * 1024
);
export const DBLegacyExamQuestionCriterionSchema = z.object({
  mark: z.number().int(), // Can be positive (additive) or negative (deductive)
  content: DBLegacyExamQuestionCriterionContentSchema,
  index: z.number().int().min(0),
});

/**
 * DB Schemas
 */

export const DBLegacyExamQuestionSubjectExamIdentifierSchema = z.string().refine(val => {
  const parts = val.split('#');

  return (
    parts.length === 2 &&
    SubjectSchema.safeParse(parts[0]).success &&
    z.lazy(() => DBLegacyExamExamIdSchema).safeParse(parts[1]).success
  );
});
export const DBLegacyExamQuestionQuestionIdSchema = UUIDSchema;
export const DBLegacyExamQuestionActiveSchema = StringBooleanSchema;
const examQuestionAnalyticsBaseSchema = z.object({
  aggregateMark: z.number().min(0),
  topMark: z.number().min(0),
  uniqueAttempts: z.number().int().min(0),
  markFrequency: z.record(z.string().regex(/^[0-9]+$/), z.number().int().min(0)).optional(),
  optionFrequency: z.record(z.string().regex(/^[0-9]+$/), z.number().int().min(0)).optional(),
});

const examQuestionAnalyticsRefine = (data: z.infer<typeof examQuestionAnalyticsBaseSchema>) =>
  data.markFrequency !== undefined || data.optionFrequency !== undefined;

const examQuestionAnalyticsRefineMessage = {
  message: 'At least one of markFrequency or optionFrequency must be defined',
};

export const DBLegacyExamQuestionAnalyticsSchema = examQuestionAnalyticsBaseSchema.refine(
  examQuestionAnalyticsRefine,
  examQuestionAnalyticsRefineMessage
);
export const DBLegacyExamQuestionCreationDatetimeSchema = UnixTimestampSchema;
export const DBLegacyExamQuestionCriteriaSchema = z.record(
  DBLegacyExamQuestionCriterionIdSchema,
  DBLegacyExamQuestionCriterionSchema
);
export const DBLegacyExamQuestionEditDatetimeSchema = UnixTimestampSchema;
export const DBLegacyExamQuestionGuidesSchema = z.partialRecord(
  DBLegacyExamQuestionGuideTypeSchema,
  z.object({
    content: DBLegacyExamQuestionGuideContentSchema,
    index: z.number().int().min(0),
    label: DBLegacyExamQuestionGuideLabelSchema,
  })
);
export const DBLegacyExamQuestionLabelSchema = z.string().refine(val => {
  const splitLabel = val.split('#');
  if (splitLabel.length > 3) return false;
  if (
    !z
      .string()
      .regex(/^[1-9][0-9]*$/)
      .safeParse(splitLabel[0]).success
  )
    return false;
  if (
    splitLabel[1] &&
    !z
      .string()
      .regex(/^[a-z]$/)
      .safeParse(splitLabel[1]).success
  )
    return false;
  if (
    splitLabel[2] &&
    !z
      .string()
      .regex(/^(x?x|x?ix|x?vi{0,3}|x?i{1,3}|x?iv)$/)
      .safeParse(splitLabel[2]).success
  )
    return false;
  return true;
});
export const DBLegacyExamQuestionMaxMarkSchema = z.number().int().min(1);
export const DBLegacyExamQuestionMcqOptsSchema = z.object({
  answer: DBLegacyExamQuestionMcqOptsAnswerSchema,
  optionsCount: DBLegacyExamQuestionMcqOptsOptionsCountSchema,
});
export const DBLegacyExamQuestionTopicsSchema = z.record(
  z.lazy(() => DBLegacyTopicTopicIdSchema),
  z.object({})
);
export const DBLegacyExamQuestionTypeSchema = z.enum(QuestionType);

export const DBLegacyExamQuestionSchema = z
  .object({
    subjectExamIdentifier: DBLegacyExamQuestionSubjectExamIdentifierSchema,
    questionId: DBLegacyExamQuestionQuestionIdSchema,
    label: DBLegacyExamQuestionLabelSchema,
    maxMark: DBLegacyExamQuestionMaxMarkSchema,
    type: DBLegacyExamQuestionTypeSchema,
    creationDatetime: DBLegacyExamQuestionCreationDatetimeSchema,
    editDatetime: DBLegacyExamQuestionEditDatetimeSchema,
    active: DBLegacyExamQuestionActiveSchema,
    topics: DBLegacyExamQuestionTopicsSchema,
    mcqOpts: DBLegacyExamQuestionMcqOptsSchema.optional(),
    // Optional to allow removal of criteria points
    criteria: DBLegacyExamQuestionCriteriaSchema.optional(),
    // Optional to allow removal of guides
    guides: DBLegacyExamQuestionGuidesSchema.optional(),
    analytics: DBLegacyExamQuestionAnalyticsSchema,
  })
  .refine(
    data => {
      if (data.type === QuestionType.MultipleChoice) {
        return data.mcqOpts !== undefined;
      }
      return true;
    },
    {
      message: 'mcqOpts must be defined for MULTIPLE CHOICE questions',
      path: ['mcqOpts'],
    }
  )
  .refine(
    data => {
      if (data.type !== QuestionType.MultipleChoice) {
        return data.mcqOpts === undefined;
      }
      return true;
    },
    {
      message: 'mcqOpts must be undefined for non-MULTIPLE CHOICE questions',
      path: ['mcqOpts'],
    }
  );

/**
 * Client Schemas
 */

export const LegacyExamQuestionSubjectSchema = z.lazy(() => LegacyExamSubjectSchema);
export const LegacyExamQuestionExamIdSchema = z.lazy(() => LegacyExamExamIdSchema);
export const LegacyExamQuestionQuestionIdSchema = DBLegacyExamQuestionQuestionIdSchema;
export const LegacyExamQuestionActiveSchema = z.boolean();
export const LegacyExamQuestionAnalyticsSchema = examQuestionAnalyticsBaseSchema
  .extend({ average: z.number().min(0) })
  .refine(examQuestionAnalyticsRefine, examQuestionAnalyticsRefineMessage);
export const LegacyExamQuestionCreationDatetimeSchema = DBLegacyExamQuestionCreationDatetimeSchema;
export const LegacyExamQuestionCriteriaSchema = DBLegacyExamQuestionCriteriaSchema;
export const LegacyExamQuestionEditDatetimeSchema = DBLegacyExamQuestionEditDatetimeSchema;
export const LegacyExamQuestionGuidesSchema = DBLegacyExamQuestionGuidesSchema;
export const LegacyExamQuestionLabelSchema = DBLegacyExamQuestionLabelSchema;
export const LegacyExamQuestionMaxMarkSchema = DBLegacyExamQuestionMaxMarkSchema;
export const LegacyExamQuestionMcqOptsSchema = DBLegacyExamQuestionMcqOptsSchema;
export const LegacyExamQuestionTopicsSchema = z.array(z.lazy(() => DBLegacyTopicTopicIdSchema));
export const LegacyExamQuestionTypeSchema = DBLegacyExamQuestionTypeSchema;

export const LegacyExamQuestionSchema = z
  .object({
    examSubject: LegacyExamQuestionSubjectSchema,
    examId: LegacyExamQuestionExamIdSchema,
    questionId: LegacyExamQuestionQuestionIdSchema,
    label: LegacyExamQuestionLabelSchema,
    maxMark: LegacyExamQuestionMaxMarkSchema,
    type: LegacyExamQuestionTypeSchema,
    creationDatetime: LegacyExamQuestionCreationDatetimeSchema.default(() => dayjs().valueOf()),
    editDatetime: LegacyExamQuestionEditDatetimeSchema.default(() => dayjs().valueOf()),
    active: LegacyExamQuestionActiveSchema,
    topics: LegacyExamQuestionTopicsSchema,
    mcqOpts: LegacyExamQuestionMcqOptsSchema.optional(),
    criteria: LegacyExamQuestionCriteriaSchema.default({}),
    guides: LegacyExamQuestionGuidesSchema.default({}),
    analytics: LegacyExamQuestionAnalyticsSchema,
  })
  .refine(
    data => {
      if (data.type === QuestionType.MultipleChoice) {
        return data.mcqOpts !== undefined;
      }
      return true;
    },
    {
      message: 'mcqOpts must be defined for MULTIPLE CHOICE questions',
      path: ['mcqOpts'],
    }
  )
  .refine(
    data => {
      if (data.type !== QuestionType.MultipleChoice) {
        return data.mcqOpts === undefined;
      }
      return true;
    },
    {
      message: 'mcqOpts must be undefined for non-MULTIPLE CHOICE questions',
      path: ['mcqOpts'],
    }
  );
